import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function ResetPassword({ token, onBackToLogin }) {
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordReset, setPasswordReset] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token');
    }
  }, [token]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const validatePassword = () => {
    if (formData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!validatePassword()) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/user/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          newPassword: formData.newPassword
        }),
      });

      const result = await response.json();

      if (result.success) {
        setMessage(result.message);
        setPasswordReset(true);
        setTimeout(() => {
          if (onBackToLogin) onBackToLogin();
        }, 3000);
      } else {
        setError(result.message || 'Failed to reset password');
      }
    } catch (error) {
      setError('An error occurred. Please try again.');
      console.error('Reset password error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = (password) => {
    if (password.length === 0) return { strength: 0, label: '' };
    if (password.length < 8) return { strength: 1, label: 'Too short', color: '#e74c3c' };

    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 2) return { strength: 2, label: 'Weak', color: '#e67e22' };
    if (strength <= 3) return { strength: 3, label: 'Medium', color: '#f39c12' };
    return { strength: 4, label: 'Strong', color: '#27ae60' };
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>Reset Password</h2>

        {!token ? (
          <div style={styles.errorContainer}>
            <p style={styles.errorText}>Invalid or missing reset token. Please request a new password reset link.</p>
            <button onClick={onBackToLogin} style={styles.button}>
              Request New Link
            </button>
          </div>
        ) : !passwordReset ? (
          <>
            <p style={styles.description}>
              Please enter your new password below.
            </p>

            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>New Password</label>
                <div style={styles.passwordContainer}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    placeholder="Enter new password"
                    required
                    style={styles.input}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={styles.togglePassword}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                {formData.newPassword && (
                  <div style={styles.strengthMeter}>
                    <div
                      style={{
                        ...styles.strengthBar,
                        width: `${(passwordStrength.strength / 4) * 100}%`,
                        backgroundColor: passwordStrength.color
                      }}
                    />
                  </div>
                )}
                {formData.newPassword && (
                  <span style={{ ...styles.strengthLabel, color: passwordStrength.color }}>
                    {passwordStrength.label}
                  </span>
                )}
                <small style={styles.hint}>Must be at least 8 characters long</small>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Confirm Password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm new password"
                  required
                  style={styles.input}
                />
              </div>

              {error && (
                <div style={styles.errorMessage}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !formData.newPassword || !formData.confirmPassword}
                style={{
                  ...styles.submitButton,
                  ...(loading || !formData.newPassword || !formData.confirmPassword ? styles.submitButtonDisabled : {})
                }}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          </>
        ) : (
          <div style={styles.successContainer}>
            <div style={styles.successIcon}>✓</div>
            <p style={styles.successMessage}>{message}</p>
            <p style={styles.redirectMessage}>
              Redirecting to login page...
            </p>
          </div>
        )}

        <div style={styles.footer}>
          <button
            onClick={onBackToLogin}
            style={styles.backButton}
          >
            ← Back to Login
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
    padding: '20px'
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    padding: '40px',
    maxWidth: '450px',
    width: '100%'
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    marginBottom: '10px',
    color: '#2c3e50',
    textAlign: 'center'
  },
  description: {
    color: '#7f8c8d',
    marginBottom: '30px',
    textAlign: 'center',
    lineHeight: '1.5'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  label: {
    fontWeight: '500',
    color: '#2c3e50',
    fontSize: '14px'
  },
  passwordContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  },
  input: {
    padding: '12px',
    paddingRight: '60px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
    width: '100%'
  },
  togglePassword: {
    position: 'absolute',
    right: '10px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    padding: '5px',
    color: '#3498db',
    fontWeight: '500'
  },
  strengthMeter: {
    height: '4px',
    backgroundColor: '#ecf0f1',
    borderRadius: '2px',
    overflow: 'hidden',
    marginTop: '5px'
  },
  strengthBar: {
    height: '100%',
    transition: 'all 0.3s ease'
  },
  strengthLabel: {
    fontSize: '12px',
    fontWeight: '500',
    marginTop: '2px'
  },
  hint: {
    color: '#95a5a6',
    fontSize: '12px'
  },
  errorMessage: {
    backgroundColor: '#fee',
    color: '#c33',
    padding: '12px',
    borderRadius: '4px',
    fontSize: '14px',
    border: '1px solid #fcc'
  },
  submitButton: {
    backgroundColor: '#27ae60',
    color: 'white',
    padding: '14px',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  submitButtonDisabled: {
    backgroundColor: '#95a5a6',
    cursor: 'not-allowed'
  },
  successContainer: {
    textAlign: 'center',
    padding: '20px 0'
  },
  successIcon: {
    fontSize: '48px',
    color: '#27ae60',
    marginBottom: '20px',
    backgroundColor: '#d5f4e6',
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px'
  },
  successMessage: {
    color: '#27ae60',
    fontSize: '16px',
    fontWeight: '500',
    marginBottom: '10px'
  },
  redirectMessage: {
    color: '#7f8c8d',
    fontSize: '14px'
  },
  errorContainer: {
    textAlign: 'center',
    padding: '20px'
  },
  errorText: {
    color: '#e74c3c',
    marginBottom: '20px'
  },
  button: {
    backgroundColor: '#3498db',
    color: 'white',
    padding: '12px 24px',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  footer: {
    marginTop: '30px',
    paddingTop: '20px',
    borderTop: '1px solid #ecf0f1',
    textAlign: 'center'
  },
  backButton: {
    background: 'none',
    border: 'none',
    color: '#3498db',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '500'
  }
};

export default ResetPassword;
